import { DIM_UOM_TYPES } from '@/contants/Fields';
import { stringToNumber } from '@/utilz/format';

const CUBIC_INCHES_IN_CUBIC_FOOT = 1728;
const CUBIC_CM_PER_CUBIC_METER = 1000000;

/**
 * Calculates the total cube (volume) of items based on their dimensions and quantity.
 *
 * - If unit is 'CM', it converts the volume from cubic centimeters to cubic meters.
 * - If unit is 'IN' or 'INCHES', it converts the volume from cubic inches to cubic feet.
 * - If unit is unknown, a fallback of 0.001 * quantity is used.
 *
 * All returned values are rounded to 3 decimal places and returned as a string.
 *
 */
export const calculateCube = (
  length: string | number,
  width: string | number,
  height: string | number,
  qty: string | number,
  unit: string,
): string => {
  const lengthAsNumber = stringToNumber(length);
  const widthAsNumber = stringToNumber(width);
  const heightAsNumber = stringToNumber(height);
  const qtyAsNumber = stringToNumber(qty);
  const baseVolume = lengthAsNumber * widthAsNumber * heightAsNumber;

  if (baseVolume === 0) {
    return baseVolume.toString();
  }
  if (unit === DIM_UOM_TYPES.CM || unit === 'CM') {
    const cubeCentimeters = baseVolume / CUBIC_CM_PER_CUBIC_METER;
    // Round to 3 decimal places
    const cubeCentimetersRounded = (Math.round(cubeCentimeters * 1000) / 1000).toString();
    if (cubeCentimetersRounded === '0') {
      return (Math.round(0.001 * qtyAsNumber * 1000) / 1000).toString();
    }
    return (Math.round(Number(cubeCentimetersRounded) * qtyAsNumber * 1000) / 1000).toString();
  }
  if (unit === DIM_UOM_TYPES.INCHES || unit === 'IN') {
    const cubeFeet = baseVolume / CUBIC_INCHES_IN_CUBIC_FOOT;
    const cubeFeetRounded = (Math.round(cubeFeet * 1000) / 1000).toString();
    if (cubeFeetRounded === '0') {
      return (Math.round(0.001 * qtyAsNumber * 1000) / 1000).toString();
    }
    return (Math.round(Number(cubeFeetRounded) * qtyAsNumber * 1000) / 1000).toString();
  }
  return (Math.round(0.001 * qtyAsNumber * 1000) / 1000).toString();
};

/**
 * Calculates the extended weight and returns it as a localized string.
 *
 * This function multiplies the given quantity by the given weight and rounds the product
 * to 3 decimal places. If either value is falsy, it returns 0. The result
 * is then converted to a locale-specific string format.
 */
export const calculateExtWeight = (quantity: string | number, weight: string | number): string => {
  const extWeightValue =
    quantity && weight ? Math.round(stringToNumber(quantity) * stringToNumber(weight) * 1000) / 1000 : 0;

  return extWeightValue.toLocaleString();
};

export const calculateExtVolumeValue = (quantity: string | number, cube: string | number): number => {
  return cube && quantity ? Math.round(stringToNumber(quantity) * stringToNumber(cube) * 1000) / 1000 : 0;
};

/**
 * Calculates the extended volume and returns it as a localized string.
 *
 * This function multiplies the given quantity by the cube (volume per unit),
 * rounds the result to 3 decimal places, and formats it as a locale-specific string.
 * If either input is falsy, it returns "0".
 */
export const calculateExtVolume = (quantity: string | number, cube: string | number): string => {
  const extVolumeValue = calculateExtVolumeValue(quantity, cube);

  return extVolumeValue.toLocaleString();
};
